import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import bloodmoonWordmark from '../assets/games/bloodmoon.svg';
import { Button } from '../ui/index.js';
import { SceneStack } from '../fx/SceneStack.js';
import { scenesForGame } from '../assets/scenes/index.js';
import { ParticleLayer, useDeviceTier } from '../fx/index.js';

/** Shared style — fixed back button, top-left, safe-area aware. */
const backBtnStyle = {
  position: 'fixed' as const,
  top: 'max(16px, env(safe-area-inset-top))',
  left: 'max(16px, env(safe-area-inset-left))',
  zIndex: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 44,
  height: 44,
  borderRadius: 'var(--radius-pill)',
  background: 'rgba(11,7,9,0.6)',
  border: '1px solid var(--gold-3, #a9762f)',
  color: 'var(--gold-5, #e6b866)',
  textDecoration: 'none',
};

interface TitleScreenProps {
  /** Called with the host's chosen name when they tap Create Room. */
  onCreateRoom: (name: string) => void;
}

/**
 * Stage TitleScreen — the marquee before a room exists. The Blood Moon wordmark
 * over the lantern-lit lobby scene, a "Ringmaster presents" playbill line, and the
 * host's name input → "Create lobby". Polish over the MVP: SceneStack backdrop +
 * gold ceremony styling; the create/join flow and prop contract are unchanged.
 */
export function TitleScreen({ onCreateRoom }: TitleScreenProps) {
  const [name, setName] = useState('');
  const canCreate = name.trim().length > 0;
  const { tier } = useDeviceTier();
  const isPhone = tier === 'phone';
  const manifest = useMemo(() => scenesForGame('bloodmoon'), []);

  function handleCreate() {
    const trimmed = name.trim();
    if (!trimmed) return;
    onCreateRoom(trimmed);
  }

  const content = (
    <div
      style={{
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100dvh',
        gap: 28,
        padding: '0 24px',
        textAlign: 'center',
      }}
    >
      <Link to="/" aria-label="Back to home" style={backBtnStyle}>
        <ArrowLeft size={20} strokeWidth={2} />
      </Link>

      <style>{`
        .rm-join-input::placeholder { color: rgba(230,184,102,0.3); opacity: 1; }
        .rm-join-input:focus { border-color: var(--gold-4, #c9923f); box-shadow: 0 0 0 2px rgba(201,146,63,0.5); }
      `}</style>

      <p
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 18,
          color: 'var(--gold-4, #c9923f)',
          margin: 0,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
        }}
      >
        The Ringmaster presents
      </p>

      <img
        src={bloodmoonWordmark}
        alt="Blood Moon"
        style={{
          width: isPhone ? '72%' : '400px',
          maxWidth: 400,
          display: 'block',
          filter: 'drop-shadow(0 4px 28px rgba(153,0,0,0.55))',
        }}
      />

      {/* Host name input + Create lobby */}
      <div
        style={{
          width: '100%',
          maxWidth: 360,
          background: 'rgba(11,7,9,0.72)',
          border: '1px solid var(--gold-3, #a9762f)',
          borderRadius: 'var(--radius-card)',
          padding: 'var(--space-6)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-5)',
          boxShadow: '0 12px 48px rgba(0,0,0,0.6)',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', textAlign: 'left' }}>
          <label
            htmlFor="host-name-input"
            style={{
              color: 'var(--gold-5, #e6b866)',
              fontSize: 12,
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
            }}
          >
            Your name
          </label>
          <input
            id="host-name-input"
            className="rm-join-input"
            type="text"
            autoComplete="name"
            autoCapitalize="words"
            placeholder="John"
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, 20))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreate();
            }}
            maxLength={20}
            style={{
              fontFamily: 'var(--font-ui)',
              fontSize: 18,
              background: 'rgba(0,0,0,0.4)',
              border: '1px solid var(--surface-stroke)',
              borderRadius: 12,
              color: 'var(--text-primary)',
              padding: 'var(--space-4)',
              width: '100%',
              boxSizing: 'border-box',
              outline: 'none',
              caretColor: 'var(--gold-5, #e6b866)',
            }}
          />
        </div>

        <Button variant="primary" onClick={handleCreate} disabled={!canCreate}>
          Create lobby
        </Button>
      </div>

      <Link
        to="/play"
        style={{
          fontFamily: 'var(--font-ui)',
          fontSize: 14,
          fontWeight: 500,
          color: 'var(--gold-4, #c9923f)',
          textDecoration: 'none',
        }}
      >
        Join a lobby instead
      </Link>
    </div>
  );

  return (
    <div style={{ position: 'relative', minHeight: '100dvh' }}>
      {manifest ? (
        <SceneStack manifest={manifest} phase="LOBBY">
          <ParticleLayer preset="goldDust" intensity={0.3} slot={2} />
          {content}
        </SceneStack>
      ) : (
        content
      )}
    </div>
  );
}
